<template>
  <div class="playlist-slide">
    <ImageSlide
      v-if="getSimpleType(slide.item_type) === WIDGET_TYPES.IMAGE"
      class="playlist-slide-image"
      :slide="slide"
    />

    <VideoSlide
      v-else-if="show && getSimpleType(slide.item_type) === WIDGET_TYPES.VIDEO"
      class="playlist-slide-video"
      :slide="slide"
      :isPlaying="show"
    />

    <AudioSlide
      v-else-if="show && getSimpleType(slide.item_type) === WIDGET_TYPES.AUDIO"
      class="playlist-slide-audio"
      :itemUrl="slide.item_url"
      :itemId="slide.item_id"
      :itemType="slide?.item_type"
      :mute="slide.mute ?? false"
      :volume="slide.volume ?? 1"
      :imageMode="slide.imageMode ?? 'cover'"
    />

    <div
      v-else-if="getSimpleType(slide.item_type) === APP_TYPES.RSS"
      class="playlist-slide-app-rss"
    >
      <rss-viewer
        :setting="getRSSAppSettings(slide)"
        :isPreview="isPlaylistsSidebarPreview"
      ></rss-viewer>
    </div>

    <div
      v-else-if="getSimpleType(slide.item_type) === WIDGET_TYPES.LAYER"
      :style="{ height: '100%', width: '100%' }"
    >
      <iframe
        v-if="showLayerIframe"
        :src="layerIframeSource"
        class="iframe-container"
        scrolling="no"
      ></iframe>
    </div>

    <!-- Table app -->
    <div
      v-else-if="getSimpleType(slide.item_type) === APP_TYPES.TABLE"
      class="playlist-slide-app-table"
    >
      <TableViewer v-if="show" :settings="slide" :is_visible="show" />
    </div>

    <div
      v-else-if="getSimpleType(slide.item_type) === APP_TYPES.TRAFIKLAB"
      class="playlist-slide-trafiklab-table"
    >
      <TrafiklabViewer
        :style="{ zoom: trafiklabZoom }"
        :viewerStyles="slide.viewerStyles"
        :timetable="slide.timetable"
        :appId="slide.item_id"
        :name="slide.timetable[0].stop.name"
        alt="Trafiklab app"
        @error="showPlaceholder = true"
      />
    </div>

    <div
      v-else-if="getSimpleType(slide.item_type) === APP_TYPES.WEATHER"
      class="playlist-slide-weather-table"
    >
      <WeatherViewOld v-if="isOldWeatherApp" class="weather-app" :app="slide" />
      <WeatherView v-else class="weather-app" :app="slide" />
    </div>

    <div
      v-else-if="getSimpleType(slide.item_type) === WIDGET_TYPES.PDF"
      class="playlist-slide-weather-table"
    >
      <PdfView :pdfImages="slide.pdf_content?.thumbnail_url" />
    </div>

    <div v-else-if="getSimpleType(slide.item_type) === 'youtube'" class="player-slide-youtube">
      <YoutubeSlide :slide="slide" :isMuted="isMuted" />
    </div>

    <TemplateViewer
      v-else-if="
        show &&
        (getSimpleType(slide.item_type) === WIDGET_TYPES.TEMPLATE ||
          (getSimpleType(slide.item_type) === WIDGET_TYPES.TILE && !slide.assoc_id))
      "
      :template="slide.template"
      :playlistId="playlist?.playlist_id || playlist?.wid"
    />

    <TileViewer
      v-else-if="show && getSimpleType(slide.item_type) === WIDGET_TYPES.TILE"
      :tile="slide.template"
      :playlistId="playlist?.playlist_id || playlist?.wid"
    />

    <div v-else class="playlist-background">
      Unable to view slide of type {{ getSimpleType(slide.item_type) }}
    </div>
  </div>
</template>

<script>
  import { simpleTypeMixin } from '@/helpers';

  import RssViewer from '@/components/rssViewer/RssViewer.vue';
  import TableAppWebView from '@/components/tableApp/TableAppWebView.vue';
  import ImageSlide from '@/components/player/layerContent/ImageSlide.vue';
  import VideoSlide from '@/components/player/layerContent/VideoSlide.vue';
  import YoutubeSlide from '@/components/player/layerContent/YoutubeSlide.vue';
  import WeatherView from '@/components/apps/weather/WeatherView.vue';
  import WeatherViewOld from '@/components/apps/weather/WeatherViewOld.vue';
  import TrafiklabViewer from '@/components/common/timetable/TrafiklabViewer.vue';
  import PdfView from '@/components/content/PdfView.vue';
  import TemplateViewer from '@/components/templates/viewer/TemplateViewer.vue';
  import TileViewer from '@/components/templates/viewer/TileViewer.vue';

  import { APP_TYPES, WIDGET_TYPES } from '@/models/layoutDesigner';
  import { CONTENT_TYPE_WEATHER_APP } from '@/constant/const';
  import AudioSlide from '@/components/player/layerContent/AudioSlide.vue';

  export default {
    name: 'PlaylistSlide',

    mixins: [simpleTypeMixin],

    components: {
      AudioSlide,
      RssViewer,
      TableViewer: TableAppWebView,
      ImageSlide,
      YoutubeSlide,
      WeatherView,
      WeatherViewOld,
      TrafiklabViewer,
      PdfView,
      TemplateViewer,
      TileViewer,
      VideoSlide,
    },

    props: {
      slide: {
        type: Object,
        default: () => null,
      },

      show: {
        type: Boolean,
        default: false,
      },

      trafiklabZoom: {
        type: Number,
        default: 1,
      },

      playlist: {
        type: Object,
        default: () => null,
      },
    },

    data() {
      return {
        APP_TYPES,
        WIDGET_TYPES,
        showLayerIframe: true,
        refreshCount: 0,
      };
    },

    watch: {
      show(newValue) {
        // When slide becomes visible, refresh the layer iframe
        if (newValue && this.getSimpleType(this.slide.item_type) === WIDGET_TYPES.LAYER) {
          this.refreshLayerIframe();
        }
      },
    },

    computed: {
      isPlaylistsSidebarPreview() {
        return this.$route.name === 'Playlists' && !this.$store.state.playlist.isPlaylistPreviewOn;
      },

      isOldWeatherApp() {
        return (
          this.isWeatherApp &&
          !this.slide.weather &&
          (this.slide.visibility || this.slide.background)
        );
      },

      isWeatherApp() {
        return this.slide.item_type === CONTENT_TYPE_WEATHER_APP;
      },

      layerIframeSource() {
        if (!this.slide || !this.slide.linkUrl) return '';

        // Add refresh count as a query parameter to force iframe refresh
        const baseUrl = this.slide.linkUrl;
        const hasQueryParams = baseUrl.includes('?');
        const separator = hasQueryParams ? '&' : '?';

        return `${baseUrl}${separator}_refresh=${this.refreshCount}`;
      },
    },

    methods: {
      isMuted(itemData) {
        if (this.isLoading && !itemData) {
          return true;
        }
        return itemData.mute;
      },

      refreshLayerIframe() {
        this.showLayerIframe = false;
        this.refreshCount++;

        setTimeout(() => {
          this.showLayerIframe = true;
        }, 100);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .playlist-background {
    width: 100%;
    height: 100%;
    background: white url('../../assets/img/layoutDesigner/playlist.png') no-repeat center;
    background-size: 70px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 15%;
  }

  .iframe-container {
    width: 100%;
    height: 100%;
    border: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .playlist-slide-layer-link {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;

    color: $secondaryText;
    background-color: $backgroundGrey2;
    font-size: 32px;

    i {
      font-size: 64px;
    }
  }
</style>
